import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { CodingStrategyEnum, ThirdPartyTypeEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type { ThirdPartyForm_Person } from '../fragments/person.fragment';

interface Params {
  readonly isInternal: boolean;
  readonly initialValues?: Partial<ThirdPartyForm_Person> | null;
}

export const usePersonInitialValuesFactory = ({
  isInternal,
  initialValues: pInitialValues,
}: Readonly<Params>) => {
  const {
    thirdPartyTypesCodeMap,
    genericStatusesCodeMap,
    codingStrategiesCodeMap,
  } = useReferencesContext();
  const { generateId } = useIdFactory();

  const initialValues: ThirdPartyForm_Person = useMemo(
    () => ({
      __typename: ThirdPartyTypeEnum.Person as const,
      _id: generateId(),
      code: EMPTY_STRING,
      isVip: false,
      addresses: EMPTY_ARRAY,
      color: EMPTY_STRING,
      firstName: EMPTY_STRING,
      lastName: EMPTY_STRING,
      billEmitterIds: EMPTY_ARRAY,
      birthDate: EMPTY_STRING,
      memo: EMPTY_STRING,
      internalMemo: EMPTY_STRING,
      signatureImageUri: EMPTY_STRING,
      pictureImageUri: EMPTY_STRING,
      ...omitBy(pInitialValues, isNil),
      activeMemberships: pInitialValues?.activeMemberships ?? EMPTY_ARRAY,
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigAutomatic as const,
        _id: generateId(),
        kind: CodingStrategyEnum.CodingConfigAutomatic,
        strategyId: codingStrategiesCodeMap.CodingConfigAutomatic._id,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      contactInfo: {
        __typename: 'ContactInfo' as const,
        _id: generateId(),
        email: EMPTY_STRING,
        mobileNumber: EMPTY_STRING,
        landlineNumber: EMPTY_STRING,
        professionalNumber: EMPTY_STRING,
        faxNumber: EMPTY_STRING,
        website: EMPTY_STRING,
        ...omitBy(pInitialValues?.contactInfo, isNil),
      },
      bankInfo: {
        __typename: 'BankInfo' as const,
        _id: generateId(),
        accountOwner: EMPTY_STRING,
        bankCode: EMPTY_STRING,
        branchCode: EMPTY_STRING,
        accountNumber: EMPTY_STRING,
        certificateKey: EMPTY_STRING,
        iban: EMPTY_STRING,
        swift: EMPTY_STRING,
        domiciliation: EMPTY_STRING,
        ...omitBy(pInitialValues?.bankInfo, isNil),
      },
      categoryIds: pInitialValues?.categoryIds ?? EMPTY_ARRAY,
      statusId: pInitialValues?.statusId ?? genericStatusesCodeMap.Enabled._id,
      billReceiverId: pInitialValues?.billReceiverId ?? EMPTY_ID,
      civilityId: pInitialValues?.civilityId ?? EMPTY_ID,
      isInternal,
      kind: thirdPartyTypesCodeMap.Person.code,
      typeId: thirdPartyTypesCodeMap.Person._id,
    }),
    [
      generateId,
      genericStatusesCodeMap.Enabled._id,
      isInternal,
      pInitialValues,
      thirdPartyTypesCodeMap.Person._id,
      thirdPartyTypesCodeMap.Person.code,
      codingStrategiesCodeMap.CodingConfigAutomatic._id,
    ],
  );

  return { initialValues };
};
